import * as kendo from "@progress/kendo-intl";
import type {
  ComputedGridConfig,
  TransformDataItemFn,
  GridDataItemRaw,
  GridColumnCellValue,
  GridDataItem,
  GridMappableColumn,
  ComputedGridColumn,
  GridRowValidationSchema,
  ComputedGridConfigOptions,
  GridDataItemMetadata,
  GridDataItemErrors,
  GridInputErrorMessage,
} from "@/types/grid";
import {
  isEmptyInputValue,
  compareInputValues,
  flattenInputErrors,
  mergeInputErrors,
} from "@base/components/input/base/utils";

/**
 * Transforms a raw content value based on the configuration column type.
 * - boolean:  if value is "true" or "Y" then true, if value is "false" or "N" then false, otherwise undetermined.
 * - date: Parse the value to a date.
 * - number: Parse the value to a number.
 * - select: Converts the value to a corresponding list item with a value that matches the raw content.
 *
 */
export function transformDataValue(
  rawContent: InputValue,
  configColumn: GridMappableColumn,
  formatOptions?: I18nFormatOptions,
) {
  const editableColumn = isEditableColumn(configColumn)
    ? configColumn
    : undefined;
  const editorNormalizedContent = editableColumn
    ? normalizeInputForEditor(rawContent, editableColumn, formatOptions)
    : rawContent;

  return normalizeInputForValidation(editorNormalizedContent, configColumn);
}

/**
 * Make sure the data item is normalized based on the column type.
 * In case normalization is not possible, the raw content is returned, leaving it for errors to be shown
 * @param items
 * @param gridConfig
 * @returns
 */
export function transformDataItem<D extends GridDataItemRaw = GridDataItemRaw>(
  item: Partial<D>,
  gridConfig: ComputedGridConfig<D>,
  formatOptions?: I18nFormatOptions,
): Partial<D> {
  const normalizedItem = $ck._.cloneDeep(item);

  gridConfig.mappableColumnsHash.forEach((column, field) => {
    const fieldContent = normalizedItem[field];
    const normalizedValue = transformDataValue(
      fieldContent,
      column,
      formatOptions,
    );
    normalizedItem[field] = normalizedValue as D[typeof field];
  });

  return normalizedItem;
}

export function transformDataItems(
  items: GridDataItemRaw[],
  gridConfig: ComputedGridConfig,
  formatOptions?: I18nFormatOptions,
  transformFn: TransformDataItemFn = transformDataItem,
) {
  return Promise.all(
    items.map((item) => transformFn(item, gridConfig, formatOptions)),
  );
}

export function isDateStringOrNumber(
  value: GridColumnCellValue,
): value is Date | string | number {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    value instanceof Date
  );
}

export function getRawDataItem<D extends GridDataItemRaw>(
  dataItem: GridDataItem<D>,
) {
  const metadataFields = [
    "$uniqueId",
    "$persistedIndex",
    "$isPersisted",
    "$skipValidation",
    "$inputDataErrors",
    "$persistentDataErrors",
    "$flatErrors",
    "$original",
    "$touched",
    "$dirty",
    "$formatted",
    "$readonly",
    "$disableRowRemoval",
    "$validatedData",
  ];
  const [metadataEntries, rawDataEntries] = $ck._.partition(
    Object.entries(dataItem),
    ([field]) => metadataFields.includes(field),
  );

  const metadata = Object.fromEntries(
    metadataEntries,
  ) as GridDataItemMetadata<D>;

  const rawData = Object.fromEntries(rawDataEntries) as Partial<D>;

  return {
    rawData,
    ...metadata,
  };
}

/**
 * Make sure the data item is normalized based on the column type.
 * In case normalization is not possible, the raw content is returned, leaving it for errors to be shown
 * @param items
 * @param gridConfig
 * @returns
 */
export function normalizeDataItem<D extends GridDataItemRaw = GridDataItemRaw>(
  item: Partial<D>,
  gridConfig: ComputedGridConfig<D>,
): Partial<D> {
  const normalizedItem = $ck._.cloneDeep(item);

  gridConfig.mappableColumnsHash.forEach((column, field) => {
    const fieldContent = normalizedItem[field];
    const normalizedValue = normalizeInputForValidation(fieldContent, column);
    normalizedItem[field] = normalizedValue as D[typeof field];
  });

  return normalizedItem;
}

export function normalizeDataItems(
  items: GridDataItemRaw[],
  gridConfig: ComputedGridConfig,
) {
  return items.map((item) => normalizeDataItem(item, gridConfig));
}

function omitReadonlyFieldsFromSchema<D extends GridDataItemRaw>(
  validationSchema: GridRowValidationSchema<D>,
  { $readonly }: GridDataItem<D>,
) {
  if (typeof $readonly === "object") {
    const validationObject = $ck.utils.unwrapZodObject(validationSchema);

    const omitFields = Object.entries($readonly)
      .filter(([, value]) => value)
      .map(([key]) => key)
      .map((field) => [field, true]);

    return !omitFields.length
      ? validationSchema
      : validationObject.omit(Object.fromEntries(omitFields));
  }

  return $readonly === true ? undefined : validationSchema;
}

/**
 * Execute zod safeParseAsync and extract the errors from the result
 * @param dataItem
 * @param validationSchema
 * @returns the new dataItem with the errors and transformed data by zod
 */
export async function validateDataItem<D extends GridDataItemRaw>(
  dataItem: GridDataItem<D>,
  {
    gridConfig,
    validationSchema,
    extraErrors,
  }: {
    gridConfig: ComputedGridConfig<D>;
    validationSchema?: GridRowValidationSchema<D>;
    extraErrors?: Partial<GridDataItemErrors<D>>;
  },
) {
  if (!validationSchema) return dataItem;

  const { rawData, $persistentDataErrors, $uniqueId } =
    getRawDataItem(dataItem);

  const newValidationSchema = omitReadonlyFieldsFromSchema<D>(
    validationSchema,
    dataItem,
  );

  if (!newValidationSchema) {
    const $inputDataErrors = extraErrors as GridDataItemErrors<D>;

    return {
      $validatedData: undefined,
      $inputDataErrors,
      $persistentDataErrors,
      $flatErrors: flattenDataItemErrors({
        $uniqueId,
        $inputDataErrors,
        $persistentDataErrors,
      }),
    };
  }

  const result = await newValidationSchema.safeParseAsync(rawData);

  if (result.success && !extraErrors) {
    return {
      $validatedData: result.data as D,
      $inputDataErrors: undefined,
      $persistentDataErrors,
      $flatErrors: flattenDataItemErrors({
        $uniqueId,
        $persistentDataErrors,
      }),
    };
  }

  const zodErrors = result.error
    ? getInputDataErrors(gridConfig, result.error as $ck.z.ZodError<D>)
    : undefined;

  const fieldErrors = (zodErrors?.fieldErrors ??
    {}) as GridDataItemErrors<D>["fieldErrors"];

  const $inputDataErrors = mergeItemDataErrors(
    {
      formErrors: zodErrors?.formErrors ?? [],
      fieldErrors,
    },
    extraErrors,
  );

  return {
    $validatedData: undefined,
    $inputDataErrors,
    $flatErrors: flattenDataItemErrors({
      $uniqueId,
      $inputDataErrors,
      $persistentDataErrors,
    }),
  };
}

export function appendItemDataErrors<
  D extends GridDataItemRaw,
  T_Error extends string | GridInputErrorMessage,
  T_DataErrors extends GridDataItemErrors<D, T_Error> = GridDataItemErrors<
    D,
    T_Error
  >,
>(
  dataErrors: T_DataErrors | undefined,
  errors: T_Error[],
  field?: StringKeyOf<D>,
) {
  const extraErrors = (
    !field?.length
      ? {
          formErrors: errors,
        }
      : {
          fieldErrors: {
            [field]: errors,
          },
        }
  ) as Partial<T_DataErrors>;

  return mergeItemDataErrors(dataErrors ?? {}, extraErrors);
}

export function mergeItemDataErrors<
  D extends GridDataItemRaw,
  T_Error extends string | GridInputErrorMessage,
  T_DataErrors extends GridDataItemErrors<D, T_Error> = GridDataItemErrors<
    D,
    T_Error
  >,
>(
  dataErrors: Partial<T_DataErrors>,
  extraErrors?: Partial<T_DataErrors>,
): T_DataErrors {
  const result = {
    formErrors: mergeInputErrors(
      dataErrors.formErrors,
      extraErrors?.formErrors,
    ),
    fieldErrors: dataErrors.fieldErrors ?? {},
  } as T_DataErrors;

  Object.entries(extraErrors?.fieldErrors ?? {}).forEach(([key, errors]) => {
    const field = key as StringKeyOf<D>;
    result.fieldErrors[field] = mergeInputErrors(
      result.fieldErrors[field],
      errors,
    );
  });

  return {
    ...dataErrors,
    ...result,
  };
}

/**
 * Extract zod safeParseAsync ZodErrors into fieldErrors and formErrors,
 * fieldErrors gets propagated to other fields based on gridConfig propagateErrorsFrom
 */
export function getInputDataErrors<D extends GridDataItemRaw>(
  gridConfig: ComputedGridConfig<D>,
  validationErrors: $ck.z.ZodError<D>,
) {
  const DEFAULT_FIELD = "$formErrors";
  const flattenErrors = validationErrors.flatten();
  const errorMap: Record<string, string[]> = {
    [DEFAULT_FIELD]: [...flattenErrors.formErrors],
  };

  // for each field errors we map to the corresponding column
  // e.g a field could be hidden and its errors would propagate to another
  // or if the field isn't found, then is shown as a formError (row level error)
  Object.entries<string[] | undefined>(flattenErrors.fieldErrors).forEach(
    ([field, errors]) => {
      if (!errors?.length) return;
      const col = gridConfig.mappableColumnsHash.get(field as StringKeyOf<D>);
      const targetField: string =
        col?.propagateErrorsTo ??
        // only propagate to same field if is visible, otherwise to formErrors
        (!col?.hidden && col?.field ? col?.field : undefined) ??
        DEFAULT_FIELD;

      // Nullish coalescing assignment (??=):
      // sets errorMap[targetField] to [] only if it's null/undefined
      const targetArray = (errorMap[targetField] ??= []);
      targetArray.push(...errors);
    },
    {
      [DEFAULT_FIELD]: [...flattenErrors.formErrors],
    },
  );

  const { [DEFAULT_FIELD]: formErrors, ...fieldErrors } = errorMap;

  return {
    formErrors,
    fieldErrors,
  } as GridDataItemErrors<D>;
}

export function flattenDataItemErrors<
  D extends GridDataItemRaw = GridDataItemRaw,
>({
  $uniqueId: prefix,
  $inputDataErrors,
  $persistentDataErrors,
}: Pick<
  GridDataItemMetadata<D>,
  "$uniqueId" | "$inputDataErrors" | "$persistentDataErrors"
>) {
  const transform = (
    message: GridInputErrorMessage,
    field?: string,
  ): GridInputErrorMessage => ({
    ...message,
    field,
    dataItemId: prefix,
  });

  const inputErrors = flattenInputErrors($inputDataErrors, {
    prefix,
    transform,
  });
  const persistentErrors = flattenInputErrors($persistentDataErrors, {
    prefix,
    transform,
  });

  return mergeInputErrors(inputErrors, persistentErrors);
}

export function itemHasErrors<D extends GridDataItemRaw>(
  dataItem: GridDataItem<D>,
) {
  return !!dataItem.$flatErrors?.length;
}

export function partitionFlatErrors<D extends GridDataItemRaw>(
  dataItem: GridDataItem<D>,
) {
  const [fieldLevelErrors, rowLevelErrors] = $ck._.partition(
    dataItem.$flatErrors ?? [],
    (error) => error.field?.length,
  );

  return {
    fieldLevelErrors,
    rowLevelErrors,
  };
}

export function itemFieldHasErrors<D extends GridDataItemRaw>(
  dataItem: GridDataItem<D>,
  field: StringKeyOf<D>,
) {
  const { fieldLevelErrors } = partitionFlatErrors(dataItem);
  return fieldLevelErrors.filter((error) => error.field === field).length > 0;
}

export function makeTouchedRecord<D extends GridDataItemRaw>(
  item: GridDataItem<D>,
  touchedFields: StringKeyOf<D>[],
) {
  const touched = (item.$touched ?? {}) as NonNullable<
    GridDataItemMetadata<D>["$touched"]
  >;
  for (const field of touchedFields) {
    touched[field] = true;
  }
  return touched;
}

export function makeDirtyRecord<D extends GridDataItemRaw>(
  item: GridDataItem<D>,
  fields: StringKeyOf<D>[],
) {
  const { rawData, $validatedData, $original } = getRawDataItem(item);
  const updatedData = $validatedData ?? rawData;
  const $dirty = mapKeysToRecord(
    identifyMismatchedFields(updatedData, {
      fields,
      original: $original,
    }),
    true,
  ) as GridDataItemMetadata<D>["$dirty"];

  return $dirty;
}

function isNullUndefinedOrEmpty(value: unknown): boolean {
  return $ck._.isNil(value) || value === "";
}

export function compareItemsKey<D extends GridDataItemRaw>(
  a: Partial<D>,
  b: Partial<D>,
  uniqueKey?: StringKeyOf<D>[],
) {
  if (!uniqueKey?.length) return false;
  return uniqueKey.every(
    (key) =>
      !isNullUndefinedOrEmpty(a[key]) &&
      !isNullUndefinedOrEmpty(b[key]) &&
      compareCellValues(a[key], b[key]),
  );
}

export function compareCellValues(
  itemA: GridColumnCellValue,
  itemB: GridColumnCellValue,
): boolean {
  if (Array.isArray(itemA) || Array.isArray(itemB)) {
    if (!Array.isArray(itemA)) return false;
    if (!Array.isArray(itemB)) return false;
    return itemA.every((value, index) =>
      compareInputValues(value, itemB[index]),
    );
  }
  return compareInputValues(itemA, itemB);
}

export function getDuplicatedItems<
  D extends GridDataItemRaw,
  T_Item extends Partial<D> | D | GridDataItem<D> = Partial<D>,
  T_Compare extends Partial<D> | D | GridDataItem<D> = Partial<D>,
>(
  items: T_Item[],
  compareItem: T_Compare,
  uniqueKey: StringKeyOf<D>[],
): T_Item[] {
  return items.filter((item) => compareItemsKey(item, compareItem, uniqueKey));
}

export function identifyMismatchedFields<D extends GridDataItemRaw>(
  item: Partial<D>,
  {
    original,
    fields,
  }: {
    original?: Partial<D>;
    fields: StringKeyOf<D>[];
  },
) {
  if (!original) {
    return fields.filter((key) => !isEmptyInputValue(item[key]));
  }
  return fields.filter((key) => !compareCellValues(item[key], original![key]));
}

function defaultKendoCellFormat<D extends GridDataItemRaw>(
  column: ComputedGridColumn<D>,
  rawValue: InputSingleValue,
) {
  if (rawValue === undefined || rawValue === null) {
    return "";
  }
  if (!isDateStringOrNumber(rawValue)) {
    return rawValue.toString() ?? "";
  }

  const type = detectColumnDisplayType(column);
  switch (type) {
    case "date": {
      const value = tryParseDate(rawValue);
      if (value instanceof Date) {
        return kendo.formatDate(value, column.format ?? "d");
      }
      return value?.toString() ?? "";
    }
    case "number": {
      const numberValue = kendo.parseNumber(rawValue.toString());
      return kendo.formatNumber(numberValue, column.format ?? "n");
    }
  }
  return kendo.toString(rawValue, column.format);
}

function formatCellItemValue<D extends GridDataItemRaw>(
  rawValue: InputSingleValue,
  options: {
    column: ComputedGridColumn<D>;
    formatOptions: ComputedGridConfigOptions<D>["formatOptions"];
  },
) {
  try {
    const column = options.column;
    const formatOptions = toValue(options.formatOptions);

    if (
      column.editorProps &&
      "items" in column.editorProps &&
      column.editorProps.items
    ) {
      const listItem = findListItemByValue(
        column.editorProps.items,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rawValue as any,
      );
      const label = getListItemOptionLabel(listItem);
      if (label) {
        if (column.editorProps.translateItems) {
          return formatOptions.t(label);
        }
        return label;
      }
    }

    if (isEmptyInputValue(rawValue)) return "";

    if (isDateStringOrNumber(rawValue) && column.format !== undefined) {
      return kendo.toString(rawValue, column.format);
    } else if (
      column.editor === "date" &&
      typeof formatOptions.dateFormatI18n === "function" &&
      (typeof rawValue === "string" || rawValue instanceof Date)
    ) {
      return $ck.utils.formatDateString(rawValue, formatOptions.dateFormatI18n);
    }

    return defaultKendoCellFormat(column, rawValue);
  } catch (error) {
    devConsole.warn(
      `Error formatting cell value '${rawValue?.toString()}'`,
      error,
    );
  }

  return tryParseString(rawValue)?.toString() ?? "";
}

export function formatCellValue<D extends GridDataItemRaw>(
  modelValue: GridColumnCellValue,
  options: {
    column: ComputedGridColumn<D>;
    formatOptions: ComputedGridConfigOptions<D>["formatOptions"];
  },
) {
  const arrayOfValueItems = Array.isArray(modelValue)
    ? modelValue
    : [modelValue];

  return (
    arrayOfValueItems
      .map((v) => formatCellItemValue(v, options))
      // TODO: pending array serialization
      .join(", ")
  );
}

export function formatDataItem<D extends GridDataItemRaw>(
  data: GridDataItem<D>,
  { gridConfig }: { gridConfig: ComputedGridConfig<D> },
): GridDataItem<D> {
  const { rawData } = getRawDataItem(data);
  const formatted = Object.fromEntries(
    gridConfig.columns.filter(isMappableColumn).map((column) => [
      column.field,
      formatCellValue(rawData[column.field!], {
        column,
        formatOptions: gridConfig.options.formatOptions,
      }),
    ]),
  );

  return {
    ...data,
    $formatted: {
      ...rawData, //keep originals in any case
      ...formatted,
    },
  };
}

export async function computeAndFormatItems<D extends GridDataItemRaw>(
  originalGridData: GridDataItem<D>[],
  {
    gridConfig,
  }: {
    gridConfig?: ComputedGridConfig<D>;
  },
) {
  if (!gridConfig) return originalGridData;

  const computedData = gridConfig.computeItemFn
    ? ((await Promise.all(
        originalGridData.map((item, rowIndex) =>
          gridConfig.computeItemFn!(item, {
            rowIndex,
            gridData: originalGridData,
          }),
        ),
      )) as GridDataItem<D>[])
    : originalGridData;

  const gridData = computedData.map((item) =>
    formatDataItem(item, {
      gridConfig,
    }),
  );
  return gridData;
}

export function mapKeysToRecord<K extends string, V = boolean>(
  keys: K[],
  mapFnOrValue: V | ((k: K) => V) = true as V,
  options?: {
    compact: boolean;
  },
): Record<K, V> {
  const mapFn = (
    typeof mapFnOrValue === "function" ? mapFnOrValue : () => mapFnOrValue
  ) as (k: K) => V;

  const mappedEntries = keys.map((key) => [key, mapFn(key)]);
  const entries = options?.compact
    ? mappedEntries.filter(([_, v]) => v !== undefined)
    : mappedEntries;

  return Object.fromEntries(entries) as Record<K, V>;
}

export function mapObjToRecord<
  D extends Record<string, unknown> = GridDataItemRaw,
  K extends StringKeyOf<D> = StringKeyOf<D>,
  P = D[K],
  V = P,
>(
  item: D,
  mapFnOrValue?: V | ((value: P, key: K, item: D) => V),
  options?: {
    compact: boolean;
  },
): Record<K, V> {
  const keys = Object.keys(item) as K[];

  const mapFn = (
    typeof mapFnOrValue === "function"
      ? mapFnOrValue
      : typeof mapFnOrValue !== "undefined"
        ? () => mapFnOrValue
        : (v) => v
  ) as (value: P, key: K, item: Partial<D>) => V;

  return mapKeysToRecord(
    keys,
    (key) => mapFn(item[key] as P, key, item),
    options,
  );
}

export function makeGridDataItem<D extends GridDataItemRaw>(
  rawData: Partial<D>,
): GridDataItem<D>;
export function makeGridDataItem<D extends GridDataItemRaw>(
  rawData: D,
  persistedIndex: number,
): GridDataItem<D>;
export function makeGridDataItem<D extends GridDataItemRaw>(
  rawData: D | Partial<D>,
  persistedIndex?: number,
): GridDataItem<D> {
  const result = { ...rawData } as unknown as GridDataItem<D>;

  result.$isPersisted ??= $ck._.isNil(persistedIndex) ? false : true;
  result.$original = result.$isPersisted ? (rawData as D) : undefined;
  result.$persistedIndex ??= result.$isPersisted ? persistedIndex : undefined;
  result.$uniqueId ??= result.$isPersisted
    ? `persisted-${persistedIndex}`
    : $ck._.uniqueId("new-");

  return result;
}

export function clonePersistentItems<
  D extends GridDataItemRaw = GridDataItemRaw,
>(originalDataItems: D[]): GridDataItem<D>[] {
  return originalDataItems.map((item, index) =>
    makeGridDataItem($ck._.cloneDeep(item), index),
  );
}

export function initializeNewItems<D extends GridDataItemRaw = GridDataItemRaw>(
  rawItems: Partial<D>[],
): GridDataItem<D>[] {
  return rawItems.map((rawData) => makeGridDataItem(rawData));
}

export function isItemDirty<D extends GridDataItemRaw = GridDataItemRaw>(
  dataItem: GridDataItem<D> | D,
) {
  if (!("$dirty" in dataItem)) return false;

  return (
    // new items or updated items are always $dirty
    !$ck._.isNil(dataItem.$dirty) &&
    $ck._.some((dataItem as GridDataItem<D>).$dirty)
  );
}

export function isItemTouched<D extends GridDataItemRaw = GridDataItemRaw>(
  dataItem: GridDataItem<D>,
) {
  return dataItem.$touched && $ck._.some(dataItem.$touched);
}

export function makeDataChangesPayload<
  D extends GridDataItemRaw = GridDataItemRaw,
>(
  dataItems: (D | GridDataItem<D>)[],
  originalItems: D[],
  uniqueKey?: StringKeyOf<D>[],
  reason?: string,
) {
  const deletedItems = originalItems.filter(
    (original) =>
      !dataItems.some((item) => compareItemsKey(original, item, uniqueKey)),
  );

  const dirtyItems = dataItems.filter(isItemDirty).map((item) => {
    const { rawData, $validatedData } = getRawDataItem(item as GridDataItem<D>);
    return $validatedData ?? rawData;
  });

  const [updatedItems, addedItems] = $ck._.partition(dirtyItems, (item) =>
    originalItems.some((original) =>
      compareItemsKey(original, item, uniqueKey),
    ),
  );

  if (!updatedItems.length && !addedItems.length && !deletedItems.length) {
    return;
  }

  return {
    addedItems,
    updatedItems,
    deletedItems,
    reason,
  };
}

export function gridHasUnsavedChanges<
  D extends GridDataItemRaw = GridDataItemRaw,
>({
  dataItems,
  deletedItems,
}: {
  dataItems: GridDataItem<D>[];
  deletedItems: GridDataItem<D>[];
}) {
  const originalDeletedItems = deletedItems.filter(
    (item) => item.$original !== undefined,
  );
  if (originalDeletedItems.length > 0) {
    return true;
  }

  return dataItems.some(isItemDirty);
}

export function getRowLevelErrorStatus<
  D extends GridDataItemRaw = GridDataItemRaw,
>(dataItem: GridDataItem<D>) {
  const { rowLevelErrors } = partitionFlatErrors(dataItem);

  if (rowLevelErrors.some(({ type }) => type === "error")) {
    return "error";
  }

  if (rowLevelErrors.some(({ type }) => type === "warning")) {
    return "warning";
  }
}
